import { twMerge } from 'tailwind-merge';
import { TranscriptionExtended } from 'flow/types/CallRecord';
import { CallRecordKey } from 'flow/types/EntityKeys';
import { useCallRecordDisplayTimestamp } from 'flow/hooks/useCallRecordDisplayTimestamp';
import SectionCallType from '../call-event/SectionCallType';
import DateSectionHeader from '../call-event/DateSectionHeader';
import SectionCounterpartVerification from '../../pbx-queue-items/history/call-record/SectionCounterpartVerification';
import SectionCallDetails from './SectionCallDetails';
import SectionRecordingAndTranscription from 'flow/components/call-history-v2/transcription/SectionRecordingAndTranscription';
import MarkAsHandledButtonV2 from '../../misc/MarkAsHandledButtonV2';
import {
    PageType,
    SegmentContextType,
} from 'flow/components/call-history-v2/types';

interface Props {
    callRecordKey: CallRecordKey;
    segmentContext: SegmentContextType;
    extendedTranscription?: TranscriptionExtended;
    contentClassName?: string;
    dateLabelBgClassName?: string;
    scrollableId?: string;
    // On the PBX page we don't want to expose recordings/transcriptions that the API only
    // returns to their owner. Even though there's no actual leakage, surfacing them would
    // make the viewer wonder whether other people can see their personal content.
    hidePersonalContent?: boolean;
    pageType: PageType;
}

const LogTabContent = ({
    callRecordKey,
    segmentContext,
    extendedTranscription,
    contentClassName,
    dateLabelBgClassName,
    scrollableId,
    hidePersonalContent,
    pageType,
}: Props) => {
    const displayTimestamp = useCallRecordDisplayTimestamp(
        callRecordKey,
        pageType,
    );

    if (!displayTimestamp) return null;

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div
                id={scrollableId}
                className={twMerge(
                    'scrollbar scrollbar-gutter-stable flex flex-1 justify-center overflow-y-auto',
                    contentClassName,
                )}
            >
                <div className="flex w-full max-w-[600px] flex-col gap-3">
                    <DateSectionHeader
                        date={displayTimestamp}
                        bgClassName={dateLabelBgClassName}
                    />
                    <div className="flex flex-col gap-1 pb-4">
                        <SectionCallType
                            callRecordKey={callRecordKey}
                            pageType={pageType}
                        />
                        <SectionCallDetails
                            callRecordKey={callRecordKey}
                            pageType={pageType}
                        />
                        <SectionRecordingAndTranscription
                            callRecordKey={callRecordKey}
                            extendedTranscription={extendedTranscription}
                            hidePersonalContent={hidePersonalContent}
                            pageType={pageType}
                        />
                        <SectionCounterpartVerification
                            callRecordKey={callRecordKey}
                        />
                        <div className="py-2">
                            <MarkAsHandledButtonV2
                                callRecordKey={callRecordKey}
                                segmentContext={segmentContext}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LogTabContent;
